import React from 'react';
import CopyButton from '../../components/ui/CopyButton';

/** `2026-09-12T06:49:00.000Z` → `2026-09-12 06:49` (UTC, ahogy a szerver küldi). */
const stamp = (iso) => (iso ? String(iso).slice(0, 16).replace('T', ' ') : '—');
const euro = (n) => `€${Number(n || 0).toFixed(2)}`;
const durationLabel = (days) =>
  days == null ? '—' : days >= 36000 ? 'Lifetime' : `${days} ${days === 1 ? 'day' : 'days'}`;

export const STATUS_TONES = {
  delivered: 'bg-emerald-500/12 text-emerald-300 border-emerald-500/25',
  pending:   'bg-amber-500/12 text-amber-300 border-amber-500/25',
  confirmed: 'bg-sky-500/12 text-sky-300 border-sky-500/25',
  expired:   'bg-white/[0.05] text-gray-500 border-white/[0.08]',
};

function Badge({ tone, children, title }) {
  return (
    <span title={title}
          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border capitalize whitespace-nowrap ${tone}`}>
      {children}
    </span>
  );
}

function SubTitle({ children, count }) {
  return (
    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-2 mt-5 first:mt-0">
      {children}{count != null && <span className="text-gray-700"> · {count}</span>}
    </p>
  );
}

function KeyChip({ value }) {
  if (!value) return <span className="text-gray-700">—</span>;
  return (
    <span className="inline-flex items-center gap-1 max-w-full rounded-md px-1.5 py-0.5
                     bg-black/30 border border-white/[0.07]" title={value}>
      <span className="font-mono text-[11px] text-brand-200 truncate">{value}</span>
      <CopyButton value={value} variant="icon" className="w-5 h-5" />
    </span>
  );
}

/**
 * Egy fiók vásárlási előzménye: rendelések + kulcs-aktiválások.
 *
 * Két lista, mert két különböző dolog: a RENDELÉS az, amit a boltban vett, az
 * AKTIVÁLÁS az, amitől tényleg futott az előfizetése. A kettő eltérhet (bolton
 * kívüli kulcs, ajándékba adott kulcs) — az eltéréseket a fejléc ki is mondja,
 * mert pont ezekre kérdez rá a support.
 *
 * Adatot NEM kér le maga: a profil oldal egy kérésben hozza, ez csak kirajzol.
 */
export default function PurchaseHistorySection({ data }) {
  const s = data.summary;

  return (
    <div>
      {/* Az eltérések — ezek nélkül a két lista egymás mellett félreérthető. */}
      <div className="space-y-1.5 text-xs mb-1">
        {data.user.lifetime && (
          <p className="text-sky-300">
            This account has a lifetime staff license ({data.user.role}) — it does not need a subscription.
          </p>
        )}
        {s.pending + s.expired > 0 && (
          <p className="text-gray-500">
            {s.pending > 0 && <>{s.pending} unpaid pending order(s). </>}
            {s.expired > 0 && <>{s.expired} expired order(s) that were never paid.</>}
          </p>
        )}
        {s.keysActivatedByOthers > 0 && (
          <p className="text-amber-300">
            {s.keysActivatedByOthers} key(s) bought by this account were activated by another account.
          </p>
        )}
        {s.activationsWithoutOwnOrder > 0 && (
          <p className="text-amber-300">
            {s.activationsWithoutOwnOrder} activation(s) used a key this account did not buy in the shop.
          </p>
        )}
      </div>

      <SubTitle count={data.orders.length}>Orders</SubTitle>
      {data.orders.length === 0 ? (
        <p className="text-sm text-gray-600 surface-sunken px-4 py-3">No orders placed in the shop.</p>
      ) : (
        <div className="rounded-lg border border-white/[0.06] divide-y divide-white/[0.05]">
          {data.orders.map(o => (
            <div key={o.id} className="px-4 py-3 text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs text-gray-400">{stamp(o.created_at)}</span>
                <span className="text-white font-medium">{o.plan_label || '—'}</span>
                <Badge tone={STATUS_TONES[o.status] || STATUS_TONES.expired}>{o.status}</Badge>
                <span className="ml-auto tabular-nums text-gray-200">
                  {o.amount_paid != null && o.amount_paid !== o.final_price ? (
                    <>
                      {euro(o.amount_paid)}
                      <span className="text-gray-600 line-through ml-1.5 text-xs">{euro(o.final_price)}</span>
                    </>
                  ) : euro(o.final_price)}
                </span>
              </div>

              <div className="mt-1.5 flex items-center gap-x-3 gap-y-1 flex-wrap text-xs text-gray-500">
                <span>#{o.id} · {o.payment_method}</span>
                {o.promo_code && <span>promo <span className="text-gray-300">{o.promo_code}</span></span>}
                {o.referral_code && (
                  <span>referral <span className="text-brand-200">{o.referral_code}</span>
                    {o.referrer_username && <> ({o.referrer_username})</>}</span>
                )}
                {o.delivered_at && <span>delivered {stamp(o.delivered_at)}</span>}
              </div>

              {o.key_value && (
                <div className="mt-2 flex items-center gap-2 flex-wrap text-xs">
                  <KeyChip value={o.key_value} />
                  {o.key_days != null && o.key_days !== o.plan_days && (
                    <span className="text-amber-400/80" title="The key's own duration is what the license runs for.">
                      key: {durationLabel(o.key_days)}
                    </span>
                  )}
                  {!o.key_is_used && <span className="text-gray-500">not activated yet</span>}
                  {o.key_is_used && !o.key_used_by_other && (
                    <span className="text-gray-500">activated {stamp(o.key_used_at)}</span>
                  )}
                  {o.key_used_by_other && (
                    <span className="text-amber-300">
                      activated by <strong>{o.key_used_by}</strong> {stamp(o.key_used_at)}
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <SubTitle count={data.activations.length}>Key activations</SubTitle>
      {data.activations.length === 0 ? (
        <p className="text-sm text-gray-600 surface-sunken px-4 py-3">No key was ever activated on this account.</p>
      ) : (
        <div className="rounded-lg border border-white/[0.06] divide-y divide-white/[0.05]">
          {data.activations.map(a => {
            const expired = !a.is_frozen && a.expires_at && new Date(a.expires_at) <= new Date();
            const state = a.is_frozen ? ['frozen', STATUS_TONES.confirmed]
              : a.is_current && !expired ? ['active', STATUS_TONES.delivered]
              : ['ended', STATUS_TONES.expired];
            return (
              <div key={a.id} className="px-4 py-3 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-gray-400">{stamp(a.activated_at)}</span>
                  <span className="text-white font-medium">{a.plan_label || a.product_name || '—'}</span>
                  <Badge tone={state[1]}>{state[0]}</Badge>
                  <span className="ml-auto text-xs text-gray-500">
                    {durationLabel(a.key_days)} · until {stamp(a.expires_at).slice(0, 10)}
                  </span>
                </div>
                <div className="mt-2 flex items-center gap-2 flex-wrap text-xs">
                  <KeyChip value={a.key_value} />
                  {a.source === 'own' && <span className="text-gray-500">from order #{a.order_id}</span>}
                  {a.source === 'other' && (
                    <span className="text-amber-300">
                      bought by <strong>{a.order_buyer}</strong> (order #{a.order_id})
                    </span>
                  )}
                  {a.source === 'none' && (
                    <span className="text-amber-300"
                          title="No shop order delivered this key — e.g. an owner-generated or hand-given key.">
                      key not from the shop
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
